import { AxiosInstance, AxiosResponse } from "axios"
import { QueryInstance } from "types/engine.types"
import {
  EXPORT_FORMAT,
  EXPORT_VARIABLE_TYPES,
  SPS_EXPORT_TYPE,
  CSV_CAT_OPTION,
} from "types/engineExport.types"
import { ExportMapProfile } from "types/exportMap.types"
import { resolveAsyncBase64 } from "api/Engine/export"

/**
 * Export Map API functions
 *
 * Functions for managing export map profiles and applying them during SPSS/CSV export.
 * Export maps control variable names and category codes in the exported data.
 */

/**
 * Generates an export map CSV from the current query instance data structure.
 * Runs DUMP on the query instance and converts the JSON structure to CSV format.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param instanceId - The query instance ID to generate export map from
 * @param varType - Variable naming method: "numbering" (A1, A2) or "labels" (actual labels)
 * @returns CSV string with LABEL, TYPE, VARIABLE, VALUES columns
 */
export async function generateExportMap(
  axiosInstance: AxiosInstance,
  instanceId: QueryInstance,
  varType: EXPORT_VARIABLE_TYPES = EXPORT_VARIABLE_TYPES.NUMBERING
): Promise<AxiosResponse<string>> {
  const response = await axiosInstance.get<string>(
    `/exportmap-generate?instanceId=${instanceId}&varType=${varType}`,
    { responseType: "text" }
  )
  return response
}

/**
 * Options for exporting with a custom export map
 */
export interface ExportWithMapOptions {
  /** Export format: "sps" for SPSS, "csv" for CSV */
  format?: EXPORT_FORMAT
  /** Variable naming method: "labels" or "numbering" */
  varType?: EXPORT_VARIABLE_TYPES
  /** SPS export type: "sps", "dat", or "sps_dat" (both) */
  spsType?: SPS_EXPORT_TYPE
  /** Filename for files inside the ZIP (without extension) */
  filename?: string
  /** Categorical export option for CSV exports */
  catOption?: CSV_CAT_OPTION
}

/**
 * Decodes a base64 string to a Blob
 */
function base64ToBlob(
  base64: string,
  mimeType: string = "application/zip"
): Blob {
  const binaryString = atob(base64)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return new Blob([bytes], { type: mimeType })
}

/**
 * Exports data with a custom export map applied.
 * This performs the full export flow: DUMP -> create data instance -> apply export map -> export.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param instanceId - The query instance ID to export from
 * @param exportMapCsv - The export map CSV content (variable codes and category mappings)
 * @param options - Export options (format, varType, spsType)
 * @returns Blob response with exported file (decoded from base64 zip)
 */
export async function exportWithMap(
  axiosInstance: AxiosInstance,
  instanceId: QueryInstance,
  exportMapCsv: string,
  options: ExportWithMapOptions = {}
): Promise<AxiosResponse<Blob>> {
  const {
    format = EXPORT_FORMAT.SPS,
    varType = EXPORT_VARIABLE_TYPES.LABELS,
    spsType = SPS_EXPORT_TYPE.SPS_DAT,
    filename,
    catOption = CSV_CAT_OPTION.NORMAL,
  } = options

  // Build params based on export format
  const params = new URLSearchParams({
    instanceId,
    format,
    varType,
  })

  if (filename) {
    params.append("project", filename)
  }

  // Add format-specific parameters
  if (format === EXPORT_FORMAT.CSV) {
    params.append("cat_option", catOption)
  } else {
    // SPS format
    params.append("spsType", spsType)
  }

  // Backend returns base64-encoded ZIP (possibly async for large SAV exports),
  // so request as text, resolve any async job, then decode.
  const response = await axiosInstance.post<string>(
    `/exportmap-post?${params.toString()}`,
    exportMapCsv,
    {
      headers: { "Content-Type": "text/plain", "ASync-Request": 10 },
      responseType: "text",
    }
  )

  // Resolve the base64 payload (200 direct, or 202 -> poll -> get), then decode to Blob.
  const base64Data = await resolveAsyncBase64(axiosInstance, response)
  const blob = base64ToBlob(base64Data)

  // Return response with Blob data
  return {
    ...response,
    data: blob,
  } as AxiosResponse<Blob>
}

/**
 * Lists all saved export map profiles for a project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param projectId - The project ID to list profiles for
 * @returns Array of export map profile metadata
 */
export async function listExportMapProfiles(
  axiosInstance: AxiosInstance,
  projectId: string
): Promise<AxiosResponse<ExportMapProfile[]>> {
  const response = await axiosInstance.get<ExportMapProfile[]>(
    `/exportmap-list?id=${projectId}`
  )
  return response
}

/**
 * Saves an export map CSV as a named profile.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param projectId - The project ID to save profile to
 * @param profileName - Name for the profile (will be sanitized)
 * @param exportMapCsv - The export map CSV content
 * @returns Response confirming the profile was saved
 */
export async function saveExportMapProfile(
  axiosInstance: AxiosInstance,
  projectId: string,
  profileName: string,
  exportMapCsv: string
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.post(
    `/exportmap-save?id=${projectId}&profile=${encodeURIComponent(
      profileName
    )}`,
    exportMapCsv,
    {
      headers: { "Content-Type": "text/plain" },
    }
  )
  return response
}

/**
 * Loads an export map CSV by profile name.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param projectId - The project ID to load profile from
 * @param profileName - Name of the profile to load
 * @returns CSV string with the export map content
 */
export async function getExportMapProfile(
  axiosInstance: AxiosInstance,
  projectId: string,
  profileName: string
): Promise<AxiosResponse<string>> {
  const response = await axiosInstance.get<string>(
    `/exportmap-get?id=${projectId}&profile=${encodeURIComponent(profileName)}`,
    { responseType: "text" }
  )
  return response
}

/**
 * Deletes an export map profile.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param projectId - The project ID to delete profile from
 * @param profileName - Name of the profile to delete
 * @returns Response confirming the profile was deleted
 */
export async function deleteExportMapProfile(
  axiosInstance: AxiosInstance,
  projectId: string,
  profileName: string
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.delete(
    `/exportmap-delete?id=${projectId}&profile=${encodeURIComponent(
      profileName
    )}`
  )
  return response
}

/**
 * Gets the original map.csv file from imported data (if available).
 * This is used to merge original codes with the generated export map.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param projectId - The project ID to get original map from
 * @returns CSV string with original map content, or error if not found
 */
export async function getOriginalMapFile(
  axiosInstance: AxiosInstance,
  projectId: string
): Promise<AxiosResponse<string>> {
  const response = await axiosInstance.get<string>(
    `/datafile-get?id=${projectId}&subtype=map&encode=text`,
    { responseType: "text" }
  )
  return response
}

/**
 * Exports data as CSV directly (without custom export map).
 * This is a simpler export that uses the default variable naming.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param instanceId - The query instance ID to export from
 * @param varType - Variable naming method: "labels" or "numbering"
 * @param catOption - Categorical export option
 * @returns Blob response with exported ZIP file (decoded from base64)
 */
export async function exportCSV(
  axiosInstance: AxiosInstance,
  instanceId: QueryInstance,
  varType: EXPORT_VARIABLE_TYPES = EXPORT_VARIABLE_TYPES.LABELS,
  catOption: CSV_CAT_OPTION = CSV_CAT_OPTION.NORMAL,
  filename: string = "export"
): Promise<AxiosResponse<Blob>> {
  const response = await axiosInstance.get<string>(
    `/export-csv?instanceId=${instanceId}&varType=${varType}&cat_option=${catOption}&project=${encodeURIComponent(filename || "export")}`,
    { responseType: "text" }
  )

  // Decode base64 to Blob
  const blob = base64ToBlob(response.data)

  return {
    ...response,
    data: blob,
  } as AxiosResponse<Blob>
}
